import React from "react";
import { FiCheck, FiX, FiZap } from "react-icons/fi";
import "./ThreeStepIndicator.css";

type OrderStatus = "pending" | "accepted" | "queued" | "played" | "declined";

interface Props {
  status: OrderStatus;
  labels?: [string, string, string]; 
  isPriority?: boolean; 
}

export default function ThreeStepIndicator({
  status,
  labels = ["Request", "Waiting for Reply", "Accepted"],
  isPriority = false,
}: Props) {
  const isAcceptedLike = ["accepted", "queued", "played"].includes(status);
  const isDeclined = status === "declined";

  const displayLabels: [string, string, string] = [
    labels[0],
    labels[1],
    isDeclined ? "Declined" : labels[2],
  ];

  const doneSet = new Set<number>(isAcceptedLike ? [0, 1, 2] : [0]);

  const isSecondActive = !isAcceptedLike && !isDeclined;

  const theme = isPriority ? "gold" : "green";
  const link12 = theme;
  const link23 = isDeclined ? "red" : isAcceptedLike ? theme : "muted";

  const DoneIcon = isPriority ? FiZap : FiCheck;

  return (
    <div
      className={`stepper3 ${isDeclined ? "stepper3--declined" : ""} ${
        isPriority ? "stepper3--priority" : ""
      }`}
      aria-label="progress"
    >
      <div className={`st ${doneSet.has(0) ? "st--done" : ""}`}>
        <span className="st__dot">
          {doneSet.has(0) ? (
            <DoneIcon
              className={`st__icon ${isPriority ? "st__zap" : "st__check"}`}
              aria-hidden
            />
          ) : (
            <span className="st__num">1</span>
          )}
        </span>
        <span className="st__label">{displayLabels[0]}</span>
      </div>

      <div className={`st__link st__link--${link12}`} aria-hidden />

      <div
        className={`st ${doneSet.has(1) ? "st--done" : ""} ${
          isSecondActive ? "st--active" : ""
        }`}
      >
        <span className="st__dot">
          {doneSet.has(1) ? (
            <DoneIcon
              className={`st__icon ${isPriority ? "st__zap" : "st__check"}`}
              aria-hidden
            />
          ) : status === "pending" ? (
            <span className="st__dots" aria-hidden>
              <i></i><i></i><i></i>
            </span>
          ) : (
            <span className="st__num">2</span>
          )}
        </span>
        <span className="st__label">{displayLabels[1]}</span>
      </div>

      <div className={`st__link st__link--${link23}`} aria-hidden />

      <div
        className={`st ${doneSet.has(2) ? "st--done" : ""} ${
          isDeclined ? "st--decline-end" : ""
        }`}
      >
        <span className="st__dot">
          {isDeclined ? (
            <FiX className="st__x" aria-hidden />
          ) : doneSet.has(2) ? (
            <DoneIcon
              className={`st__icon ${isPriority ? "st__zap" : "st__check"}`}
              aria-hidden
            />
          ) : (
            <span className="st__num">3</span>
          )}
        </span>
        <span className="st__label">{displayLabels[2]}</span>
      </div>
    </div>
  );
}
